import styled, { css } from 'styled-components';
import {
  COUNT_BADGE_SIZE_TOKENS,
  COUNT_BADGE_TONE_ACCENT,
  COUNT_BADGE_TONE_ERROR,
  type CountBadgeSize,
  type CountBadgeTone,
} from './CountBadge.constants';

interface RootProps {
  $tone: CountBadgeTone;
  $size: CountBadgeSize;
}

const MONO_STACK =
  "'Source Code Pro', Menlo, Monaco, Consolas, 'Courier New', monospace";

/**
 * Tone-coloured pill: muted bg / muted fg for neutral counts,
 * tinted bg / tinted fg for accent + error so the badge stands out
 * without needing a stroke. The `accent` tone is theme-aware — the
 * M3 primaryContainer pair is too dim against a light tab bar, so
 * light themes use it directly while dark themes pick the
 * primary-tinted surface used elsewhere in the dark palette.
 */
const toneStyles = ({
  $tone,
  theme: { colors = {}, isLightTheme },
}: {
  $tone: CountBadgeTone;
  theme: {
    colors?: Record<string, string | undefined>;
    isLightTheme?: boolean;
  };
}) => {
  if ($tone === COUNT_BADGE_TONE_ERROR) {
    return css`
      background: ${colors.errorContainer ?? 'rgba(255, 180, 171, 0.18)'};
      color: ${colors.error ?? '#FFB4AB'};
    `;
  }
  if ($tone === COUNT_BADGE_TONE_ACCENT) {
    return isLightTheme
      ? css`
          background: ${colors.primaryContainer ?? '#DBD9FF'};
          color: ${colors.onPrimaryContainer ?? '#0A006B'};
        `
      : css`
          background: color-mix(
            in oklch,
            ${colors.primary ?? '#C1C1FF'} 18%,
            transparent
          );
          color: ${colors.primary ?? '#C1C1FF'};
        `;
  }
  // Neutral tone — 8% onSurface mix gives a faint lift in dark mode and a
  // faint dark tint in light mode without requiring two separate tokens.
  return css`
    background: color-mix(
      in oklch,
      ${colors.onSurface ?? '#E4E1ED'} 8%,
      transparent
    );
    color: ${colors.onSurfaceVariant ?? '#A1A3B8'};
  `;
};

const sizeStyles = ({ $size }: { $size: CountBadgeSize }) => {
  const tokens = COUNT_BADGE_SIZE_TOKENS[$size];
  return css`
    font-size: ${tokens.fontSize};
    font-weight: ${tokens.fontWeight};
    padding: 0 ${tokens.padX};
    ${tokens.minDim &&
    css`
      min-width: ${tokens.minDim};
      height: ${tokens.minDim};
    `}
    ${tokens.mono &&
    css`
      font-family: ${MONO_STACK};
    `}
  `;
};

export const Root = styled.span<RootProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.full ?? '999px'};
  white-space: nowrap;
  flex-shrink: 0;
  ${sizeStyles}
  ${toneStyles}
`;
